import { useEffect, useMemo } from 'react';
import { useMatch } from 'react-router';
import type { LocationResponse } from '../api/generated';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectLastLocationId, setLastLocationId } from '../store/application/applicationSlice';
import { useLocations } from './useLocations';

export interface UseActiveLocationResult {
  // Resolved active location: the URL :locationId when on a location-scoped
  // page, otherwise the persisted hint, otherwise the first accessible location.
  activeLocationId: string | null;
  locations: LocationResponse[];
  isLoading: boolean;
  loadError: string | null;
}

/**
 * Resolves the active location across every page type. The URL is the source of
 * truth on location-scoped routes (/locations/:locationId/...); elsewhere we
 * fall back to a persisted "last location" hint, and finally to the user's
 * first accessible location. The resolved value is persisted back so the hint
 * tracks wherever the user last was.
 */
export function useActiveLocation(): UseActiveLocationResult {
  const dispatch = useAppDispatch();
  const { locations, locationsById, isLoading, loadError } = useLocations();
  const hint = useAppSelector(selectLastLocationId);

  const urlLocationId = useMatch('/locations/:locationId/*')?.params.locationId ?? null;

  const activeLocationId = useMemo(() => {
    if (urlLocationId) return urlLocationId;
    // Locations not loaded yet — trust the hint so the UI doesn't flicker.
    if (hint && (locations.length === 0 || locationsById[hint])) return hint;
    return locations[0]?.id ?? null;
  }, [urlLocationId, hint, locations, locationsById]);

  useEffect(() => {
    if (activeLocationId && activeLocationId !== hint) {
      dispatch(setLastLocationId(activeLocationId));
    }
  }, [activeLocationId, hint, dispatch]);

  return { activeLocationId, locations, isLoading, loadError };
}
